import axios from 'axios'
import config from './configs'
import { errorCode, errorMap } from './http'
import { local, KEY_USER_TOKEN } from './storage'
import router from '../router/index'
import uuidv4 from 'uuid/v4'

// 从查询字符串中删除指定参数
const removeQueryParam = (search, paramName) => {
  if (!search) return ''
  const params = new URLSearchParams(search)
  params.delete(paramName)
  const result = params.toString()
  return result ? '?' + result : ''
}

const checkTokenValid = (response, cb) => {
  if (response.status === 200) {
    if (response.data.code === 270 && response.data.error === 'CONVERSATION_OPEN_WRONG') {
      router.replace('/unauthorized')
      return
    }
    if (response.data.code === 100 && response.data.error === 'TOKEN_EXPIRED') {
      local.remove(KEY_USER_TOKEN)
      router.replace(`/login?tips=登入身份過期，請重新登入&redirect=${window.location.pathname + removeQueryParam(window.location.search, 'redirect')}`)
      return
    }

    if (response.data.code === 101 && response.data.error === 'TOKEN_INVALID') {
      local.remove(KEY_USER_TOKEN)
      router.replace(`/login?tips=登入信息無效，請重新登入&redirect=${window.location.pathname + removeQueryParam(window.location.search, 'redirect')}`)
      return
    }

    if (response.data.code === 102 && response.data.error === 'TOKEN_ABSENT') {
      local.remove(KEY_USER_TOKEN)
      router.replace(`/login?tips=登入信息無效，請重新登入&redirect=${window.location.pathname + removeQueryParam(window.location.search, 'redirect')}`)
      return
    }

    if (response.headers['x-max-gift-id'] || response.headers['X-Max-Gift-Id']) {
      response.data.maxGiftId = response.headers['x-max-gift-id'] || response.headers['X-Max-Gift-Id']
    } else if (response.headers['x-max-need-id'] || response.headers['X-Max-Need-Id']) {
      response.data.maxNeedId = response.headers['x-max-need-id'] || response.headers['X-Max-Need-Id']
    } else if (response.headers['x-max-moment-id'] || response.headers['X-Max-Moment-Id']) {
      response.data.maxMomentId = response.headers['x-max-moment-id'] || response.headers['X-Max-Moment-Id']
    }

    const { code, error, message } = response.data
    if (error) {
      if (code === errorCode.USER_RESTRICTION_DISABLE) {
        response.data.error = error
      } else if (code === errorCode.USER_RESTRICTION_INVESTIGATE) {
        response.data.error = '系統出錯，暫時無法登入'
      } else {
        response.data.error = errorMap(error || message)
      }
    }

    if (cb && typeof cb === 'function') {
      cb(response.data)
    } else {
      if (response.data.error) {
        throw new Error(response.data.error)
      }
    }

    return response.data
  }
}

const getUrl = (url) => {
  if (url.startsWith('/v1')) {
    return config.server + url
  }
  return config.server + config.reqlApi + url
}

const fetch = (method, url, { data, headers = {} } = {}, cb) => {
  let token = local.get(KEY_USER_TOKEN)
  const reqHeaders = {
    'Content-Type': 'application/json',
    ...headers
  }

  if (token) {
    reqHeaders['Authorization'] = `Bearer ${token}`
  }

  let reqConfig = {
    method,
    url: getUrl(url),
    headers: reqHeaders
  }

  if (method === 'get' || method === 'delete') {
    reqConfig.params = data
  } else {
    reqConfig.data = data
  }

  return axios(reqConfig)
    .then(res => {
      return checkTokenValid(res, cb)
    }).catch(err => {
      console.log('catch error', err, '\n')
      if (cb && typeof cb === 'function') {
        cb(null, err)
      } else {
        throw err
      }
    })
}

export const doUpload = (url, data, cb) => {
  let token = local.get(KEY_USER_TOKEN)
  const headers = {
    'Content-Type': 'multipart/form-data',
    'Authorization': `Bearer ${token}`
  }

  return axios.post(getUrl(url), data, {headers})
    .then(response => {
      return checkTokenValid(response, cb)
    }).catch(err => {
      console.log('catch error', err, '\n')
      cb && cb(null, err)
    })
}

export const doGet = (url, params = {}, cb) => {
  return fetch('get', url, { data: params }, cb)
}

// 讀取 blob 內容為文字（相容不支援 blob.text() 的舊版 webview）
const readBlobAsText = (blob) => {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = () => resolve('')
    reader.readAsText(blob)
  })
}

// 從 Content-Disposition 解析檔名（支援 filename*=UTF-8'' 與 filename= 兩種格式）
const parseFilename = (disposition) => {
  if (!disposition) {
    return ''
  }
  let match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (match) {
    try {
      return decodeURIComponent(match[1].replace(/"/g, ''))
    } catch (e) {
      return match[1].replace(/"/g, '')
    }
  }
  match = disposition.match(/filename="?([^"]+)"?/i)
  return match ? match[1] : ''
}

// 下載檔案（blob），供出貨報表匯出、託運單列印等非 JSON 回應使用
// 回傳 { blob, filename, contentType }；後端錯誤時回 JSON，統一轉為 { error }
export const doGetFile = (url, params = {}) => {
  let token = local.get(KEY_USER_TOKEN)
  const headers = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return axios({
    method: 'get',
    url: getUrl(url),
    params,
    headers,
    responseType: 'blob'
  })
    .then(res => {
      const blob = res.data
      // 後端錯誤時仍回 JSON，解析出錯誤訊息
      if (blob && blob.type === 'application/json') {
        return readBlobAsText(blob).then(text => {
          let message = '操作失敗，請稍後重試'
          try {
            const json = JSON.parse(text)
            message = errorMap(json.error || json.message) || message
          } catch (e) {
            // 無法解析時使用預設訊息
          }
          return { error: message }
        })
      }
      return {
        blob,
        contentType: blob.type,
        filename: parseFilename(res.headers['content-disposition'])
      }
    })
    .catch(() => {
      return { error: '操作失敗，請稍後重試' }
    })
}

export const doPost = (url, data, cb) => {
  return fetch('post', url, { data }, cb)
}

export const doDelete = (url, params = {}, cb) => {
  return fetch('delete', url, { data: params }, cb)
}

export const doPut = (url, data, cb) => {
  return fetch('put', url, { data }, cb)
}

export const queryString = (obj) => {
  if (!obj) {
    return null
  }
  let result = ''
  for (const param in obj) {
    let content = ''
    if (!obj[param] && obj[param] !== 0) {
      continue
    }
    if (Array.isArray(obj[param])) {
      if (obj[param].length === 0) {
        continue
      }
      content = obj[param].toString()
    } else {
      content = obj[param]
    }
    result += ((result) ? `&${param}=${encodeURIComponent(content)}` : `?${param}=${encodeURIComponent(content)}`)
  }
  return result
}

export const getQueryParam = (url, variable) => {
  if (url.indexOf('?') === -1) {
    return ''
  }
  let query = url.split('?')[1]
  let vars = query.split('&')
  for (let i = 0; i < vars.length; ++i) {
    let pair = vars[i].split('=')
    if (pair[0] === variable) {
      return pair[1]
    }
  }
}

export let DEVICE_UUID = (function () {
  let uuid = local.get('KEY_DEVICE_UUID')
  if (uuid) {
    return uuid
  }
  uuid = uuidv4()
  local.save('KEY_DEVICE_UUID', uuid)
  return uuid
}())

export default fetch
